import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    MenuItem,
    Card,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import CustomButton from '../../../src/components/CustomButton/CustomButton';
import Layout from '../../components/Wrappers/Layout';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import SOLogout from '../../components/Logout/SOLogout';
import regenerateToken from '../../components/RegenerateToken/regenerateToken';
import { getIpWhitelistAction } from '../../redux/actions/getipwhitelistaction';
import { addIpWhitelistAction } from '../../redux/actions/addipwhitelistaction';
import { deleteIpWhitelistAction } from '../../redux/actions/deleteipwhitelistaction';
import { getCustomerIdAndNameList } from '../../redux/actions/getCustomerIdAndNameListAction';
import success from '../../assets/img/success.png';
import failed from '../../assets/img/failed.png';

const useStyles = makeStyles({ table: { minWidth: 650 } });

// Shared sx for standard underline styling
const underlineSx = {
    '& .MuiInput-underline:before': { borderBottomColor: 'gray' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'gray' },
    '& .MuiInput-underline:after': { borderBottomColor: 'gray' },
};

const IpWhitelistConfig = () => {
    const classes = useStyles();
    const dispatch = useDispatch();

    // ── Add dialog state ───────────────────────────────────────────────────────
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    // Same two-state pattern for the Add dialog autocomplete
    const [addCustomerIdValue, setAddCustomerIdValue] = useState(null);
    const [addCustomerIdInput, setAddCustomerIdInput] = useState('');
    const [newIp, setNewIp] = useState('');
    const [newIpType, setNewIpType] = useState('IPv4');

    // ── Search state ───────────────────────────────────────────────────────────
    // MUI Autocomplete needs TWO separate controlled props:
    //   value      → the selected option object (null when nothing selected)
    //   inputValue → the raw text currently visible in the input box
    // Merging them into one state breaks the dropdown.
    const [searchMerchantIdValue, setSearchMerchantIdValue] = useState(null);
    const [searchMerchantIdInput, setSearchMerchantIdInput] = useState('');



    // ── Table / pagination ─────────────────────────────────────────────────────
    const [ipListData, setIpListData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // ── Delete dialog ──────────────────────────────────────────────────────────
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // ── Response dialog ────────────────────────────────────────────────────────
    const [respDialogOpen, setRespDialogOpen] = useState(false);
    const [respStatus, setRespStatus] = useState('');
    const [respMessage, setRespMessage] = useState('');

    // ── Redux ──────────────────────────────────────────────────────────────────
    const regenerateTokenRes = useSelector(({ regenerateToken }) => regenerateToken);
    const getIpWhitelistRes = useSelector(({ getIpWhitelist }) => getIpWhitelist);
    const addIpWhitelistRes = useSelector(({ addIpWhitelist }) => addIpWhitelist);
    const deleteIpWhitelistRes = useSelector(({ deleteIpWhitelist }) => deleteIpWhitelist);
    const getCustomerIdAndNameListRes = useSelector(({ getCustomerIdAndNameList }) => getCustomerIdAndNameList);

    const authToken = regenerateTokenRes?.data?.AuthToken;
    const walletOwnerId = regenerateTokenRes?.data?.partnerId || '';

    // Customer list options
    const customerOptions = [].concat(getCustomerIdAndNameListRes?.data?.currencyList || []);

    // Autocomplete filter — requires at least 3 chars
    const filterOptionsL = (options, { inputValue }) => {
        if (inputValue.length < 3) return [];
        return options.filter(o => o.includes(inputValue)).slice(0, 20);
    };

    // Extract numeric/raw ID before the first " - " or "-"
    const extractId = (value) => (value ? value.split('-')[0].trim() : '');

    // ── Load customer ID/name list on auth ────────────────────────────────────
    useEffect(() => {
        if (regenerateTokenRes?.data?.status === 'success') {
            dispatch(getCustomerIdAndNameList(
                {
                    loginDetails: { walletOwnerId },
                    actionCustomerMasterRequestVO: { customerNameOrId: '' , role: 'Merchant'  },
                },
                authToken
            ));
        }
    }, [regenerateTokenRes?.data?.status]);

    // ── Search ─────────────────────────────────────────────────────────────────
    const searchFunction = (overrideId) => {
        // Prefer the selected option; fall back to whatever the user typed
        const rawId = overrideId !== undefined
            ? overrideId
            : (searchMerchantIdValue || searchMerchantIdInput);
        const customerId = extractId(rawId);
        setPage(0);
        setIsLoading(true);
        setHasSearched(true);
        dispatch(getIpWhitelistAction(
            { loginDetails: { walletOwnerId, customerId } },
            authToken
        ));
    };

    // ── Effects ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!getIpWhitelistRes?.data?.status) return;
        const { status, description, ipWhitelistList } = getIpWhitelistRes.data;
        if (status === 'success') setIpListData(ipWhitelistList || []);
        if (status === 'failed') {
            if (description === 'NOT FOUND') setIpListData([]);
            if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
            if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
        }
        setIsLoading(false);
    }, [getIpWhitelistRes?.data]);

    useEffect(() => {
        if (!addIpWhitelistRes?.data?.status) return;
        const { status, description } = addIpWhitelistRes.data;
        setRespStatus(status);
        setRespMessage(description);
        setRespDialogOpen(true);
        if (status === 'success') {
            setNewIp('');
            setNewIpType('IPv4');
            setAddCustomerIdValue(null);
            setAddCustomerIdInput('');
            searchFunction();
        }
        if (status === 'failed') {
            if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
            if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
        }
    }, [addIpWhitelistRes?.data?.status]);

    useEffect(() => {
        if (!deleteIpWhitelistRes?.data?.status) return;
        const { status, description } = deleteIpWhitelistRes.data;
        setRespStatus(status);
        setRespMessage(description);
        setRespDialogOpen(true);
        if (status === 'success') searchFunction();
        if (status === 'failed') {
            if (description === 'session out') SOLogout(dispatch, authToken, walletOwnerId);
            if (description === 'Authorization failed') regenerateToken(dispatch, authToken, walletOwnerId);
        }
    }, [deleteIpWhitelistRes?.data?.status]);

    // ── Pagination ─────────────────────────────────────────────────────────────
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

    // ── Add IP ─────────────────────────────────────────────────────────────────
    const handleAddIp = () => {
        const rawId = addCustomerIdValue || addCustomerIdInput;
        const selectedCustomerId = extractId(rawId);
        if (!newIp.trim() || !selectedCustomerId) {
            setRespStatus('failed');
            setRespMessage('Customer ID and IP Address are required');
            setRespDialogOpen(true);
            return;
        }
        dispatch(addIpWhitelistAction(
            {
                loginDetails: { walletOwnerId, customerId: selectedCustomerId },
                actionCustomerMasterRequestVO: { ipAddress: newIp.trim(), ipType: newIpType },
            },
            authToken
        ));
    };

    // ── Delete IP ──────────────────────────────────────────────────────────────
    const openDeleteConfirm = (row) => { setDeleteTarget(row); setDeleteDialogOpen(true); };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        const rawId = searchMerchantIdValue || searchMerchantIdInput;
        dispatch(deleteIpWhitelistAction(
            {
                loginDetails: { walletOwnerId, customerId: extractId(rawId) },
                actionCustomerMasterRequestVO: { id: deleteTarget.id },
            },
            authToken
        ));
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <Layout>
                {/* ── Search Card ──────────────────────────────────────────── */}
                <Card>
                    <Grid
                        container
                        spacing={2}
                        alignItems="flex-end"
                        style={{ padding: '25px', marginTop: '0.5rem' }}
                    >
                        {/* Merchant ID — Autocomplete (no radio) */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Autocomplete
                                options={customerOptions}
                                getOptionLabel={(option) => option}
                                filterOptions={filterOptionsL}
                                value={searchMerchantIdValue}
                                inputValue={searchMerchantIdInput}
                                onChange={(_, newValue) => setSearchMerchantIdValue(newValue)}
                                onInputChange={(_, newInputValue) => setSearchMerchantIdInput(newInputValue)}
                                noOptionsText={searchMerchantIdInput.length < 3 ? 'Type at least 3 characters' : 'No options'}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Merchant Id"
                                        variant="standard"
                                        fullWidth
                                        margin="dense"
                                        sx={underlineSx}
                                        InputLabelProps={{ sx: { color: 'gray' } }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Search button */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <CustomButton style={{ padding: '8px' }} onClick={() => searchFunction()}>
                                Search <SearchIcon />
                            </CustomButton>
                        </Grid>

                        {/* Spacer pushes ADD to the right */}
                        <Grid item xs={12} sm={12} md={6} lg={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <CustomButton style={{ padding: '8px' }} onClick={() => setAddDialogOpen(true)}>
                                ADD
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Card>

                {/* ── Results Card ─────────────────────────────────────────── */}
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <Card style={{ marginTop: '10px' }}>
                        <Grid container style={{ padding: '10px' }}>
                            <Grid item xs={12}>
                                <Typography variant="body1" style={{ fontWeight: '700' }} className="themeText">
                                    IP Whitelisting Configuration
                                </Typography>
                            </Grid>

                            {hasSearched && ipListData.length === 0 ? (
                                <Grid item xs={12}>
                                    <Alert className="my-3 tableHeaderColor" style={{ color: 'white' }} variant="secondary">
                                        No Records Found!
                                    </Alert>
                                </Grid>
                            ) : (
                                <TableContainer component={Paper} className="my-3">
                                    <Table className={classes.table} size="small" aria-label="ip whitelist table">
                                        <TableHead>
                                            <TableRow className="tableHeaderColor">
                                                {['Merchant Start IP', 'IP Type', 'Action'].map((h) => (
                                                    <TableCell key={h} align="center" style={{ fontWeight: 'bold', color: 'white' }}>
                                                        {h}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[]
                                                .concat(ipListData)
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row, index) => (
                                                    <TableRow key={`${row.id}-${index}`}>
                                                        <TableCell align="center" className="themeColor" style={{ fontWeight: '600' }}>
                                                            {row.ipAddress || '-'}
                                                        </TableCell>
                                                        <TableCell align="center">{row.ipType || '-'}</TableCell>
                                                        <TableCell align="center">
                                                            <CustomButton style={{ padding: '8px' }} onClick={() => openDeleteConfirm(row)}>
                                                                <DeleteIcon style={{ color: '#fff', fontSize: '18px' }} />
                                                                &nbsp;Delete
                                                            </CustomButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25]}
                                        component="div"
                                        count={ipListData.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </TableContainer>
                            )}
                        </Grid>
                    </Card>
                )}

                {/* ── Delete confirmation dialog ───────────────────────────── */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent style={{ width: '400px' }}>
                        <Typography variant="body1">
                            Are you sure you want to delete IP <strong>{deleteTarget?.ipAddress}</strong>?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <CustomButton style={{ padding: '8px' }} onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </CustomButton>
                        <CustomButton style={{ padding: '8px' }} onClick={confirmDelete}>
                            Delete
                        </CustomButton>
                    </DialogActions>
                </Dialog>
            </Layout>

            {/* ── Add IP Dialog ─────────────────────────────────────────────── */}
            <Dialog
                open={addDialogOpen}
                onClose={() => {
                    setAddDialogOpen(false);
                    setAddCustomerIdValue(null);
                    setAddCustomerIdInput('');
                    setNewIp('');
                    setNewIpType('IPv4');
                }}
            >
                <DialogTitle>Add IP Address</DialogTitle>
                <DialogContent style={{ minWidth: '400px' }}>
                    <Grid container spacing={2} style={{ marginTop: '5px' }}>

                        {/* Customer ID Autocomplete — proper split: value (selected) + inputValue (typed) */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={customerOptions}
                                getOptionLabel={(option) => option}
                                filterOptions={filterOptionsL}
                                value={addCustomerIdValue}
                                inputValue={addCustomerIdInput}
                                onChange={(_, newValue) => setAddCustomerIdValue(newValue)}
                                onInputChange={(_, newInputValue) => setAddCustomerIdInput(newInputValue)}
                                noOptionsText={addCustomerIdInput.length < 3 ? 'Type at least 3 characters' : 'No options'}
                                componentsProps={{
                                    popper: {
                                        style: { zIndex: 1500 },
                                        placement: 'bottom-start',
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        autoFocus
                                        margin="dense"
                                        label="Customer ID"
                                        type="text"
                                        fullWidth
                                        variant="standard"
                                        sx={underlineSx}
                                        InputLabelProps={{ sx: { color: 'gray' } }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* IP Address */}
                        <Grid item xs={12}>
                            <TextField
                                label="IP Address"
                                fullWidth
                                value={newIp}
                                onChange={(e) => setNewIp(e.target.value)}
                            />
                        </Grid>

                        {/* IP Type */}
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="IP Type"
                                fullWidth
                                value={newIpType}
                                onChange={(e) => setNewIpType(e.target.value)}
                            >
                                <MenuItem value="IPv4">IPv4</MenuItem>
                                <MenuItem value="IPv6">IPv6</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setAddDialogOpen(false);
                        setAddCustomerIdValue(null);
                        setAddCustomerIdInput('');
                        setNewIp('');
                        setNewIpType('IPv4');
                    }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleAddIp();
                            setAddDialogOpen(false);
                        }}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Success dialog ─────────────────────────────────────────────── */}
            {respStatus === 'success' && (
                <Dialog open={respDialogOpen} onClose={() => setRespDialogOpen(false)}>
                    <DialogContent className="text-center" style={{ backgroundColor: '#00b9f51c' }}>
                        <img src={success} alt="success" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: '0', textAlign: 'center', fontSize: '15px' }}>{respMessage}</h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Button style={{ margin: '10px 0', color: 'white' }} variant="contained" onClick={() => setRespDialogOpen(false)}>
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* ── Failed dialog ──────────────────────────────────────────────── */}
            {respStatus === 'failed' && (
                <Dialog open={respDialogOpen} onClose={() => setRespDialogOpen(false)}>
                    <DialogContent className="text-center" style={{ backgroundColor: '#00b9f51c' }}>
                        <img src={failed} alt="failed" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: '0', textAlign: 'center', fontSize: '15px' }}>{respMessage}</h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Button style={{ margin: '10px 0', color: 'white' }} variant="contained" onClick={() => setRespDialogOpen(false)}>
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default IpWhitelistConfig;